import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, X, Loader2, Share2, Download, Printer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useModalBack } from "@/lib/modalBack";

// In-app file viewer (No Dead Ends): every file opens INSIDE the app with
// Back + X + Escape + swipe-down — never a bare browser tab with no way home.
// Pass `src` for a signed/public URL or `apiPath` for an authenticated endpoint
// (fetched with the bearer token → blob URL, so tokens never appear in URLs).
export default function FileViewer({ src, apiPath, title = "", fileType = "", onClose }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(src || "");
  const [err, setErr] = useState(false);
  const touchY = useRef(null);
  const blobRef = useRef(null);
  useModalBack(true, onClose); // phone Back closes the viewer, not the page under it

  useEffect(() => {
    let alive = true;
    let obj = "";
    if (apiPath) {
      (async () => {
        try {
          const tok = localStorage.getItem("buildflow.token");
          // Unique URL on every open: defeats CDN edge cache + browser cache
          // (cache:"no-store" alone can't bust a shared CDN keyed on the URL).
          const bust = `${apiPath}${apiPath.includes("?") ? "&" : "?"}_t=${Date.now()}`;
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${bust}`, { headers: { Authorization: `Bearer ${tok}` }, cache: "no-store" });
          if (!r.ok) throw new Error(String(r.status));
          const b = await r.blob();
          blobRef.current = b;
          obj = URL.createObjectURL(b);
          if (alive) setUrl(obj);
        } catch {
          if (alive) setErr(true);
        }
      })();
    }
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { alive = false; window.removeEventListener("keydown", onKey); if (obj) URL.revokeObjectURL(obj); };
  }, [apiPath]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPdf = fileType === "application/pdf" || /\.pdf(\?|$)/i.test(src || apiPath || "");
  const isWeb = fileType === "text/html"; // same-origin app pages shown in an iframe
  const fname = `${(title || "file").replace(/[^\w.-]+/g, "_")}${isPdf ? ".pdf" : ".png"}`;
  const doDownload = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.click();
  };
  const doShare = async () => {
    try {
      const blob = blobRef.current || (url ? await (await fetch(url)).blob() : null);
      if (blob && navigator.canShare?.({ files: [new File([blob], fname, { type: blob.type })] })) {
        await navigator.share({ files: [new File([blob], fname, { type: blob.type })], title });
      } else {
        await navigator.share({ title, url: src || url });
      }
    } catch { /* user cancelled */ }
  };
  const doPrint = () => {
    const w = window.open("", "_blank", "width=800,height=600");
    if (!w) return;
    w.document.write(isPdf || isWeb
      ? `<iframe src="${url}" style="width:100%;height:100%;border:0" onload="setTimeout(()=>{frames[0].focus();frames[0].print();},500)"></iframe>`
      : `<img src="${url}" style="max-width:100%" onload="setTimeout(()=>{window.focus();window.print();},300)">`);
    w.document.close();
  };
  const btn = "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-white/10 text-white active:scale-95";
  return createPortal(
    <div className="fixed inset-0 z-[120] flex flex-col bg-slate-950/95" data-testid="file-viewer"
      onTouchStart={(e) => { touchY.current = e.touches[0].clientY; }}
      onTouchEnd={(e) => {
        if (touchY.current != null && e.changedTouches[0].clientY - touchY.current > 90) onClose();
        touchY.current = null;
      }}>
      <div className="flex items-center gap-2 px-3 pb-2 pt-[max(env(safe-area-inset-top,8px),8px)]">
        <button onClick={onClose} data-testid="fv-back" aria-label={t("action_back")}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-white/10 text-white active:scale-95">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{title}</span>
        {(!!navigator.share) && (
          <button onClick={doShare} data-testid="fv-share" aria-label={t("fv_share")} className={btn}><Share2 className="h-5 w-5" /></button>
        )}
        <button onClick={doDownload} data-testid="fv-download" aria-label={t("fv_download")} className={btn}><Download className="h-5 w-5" /></button>
        <button onClick={doPrint} data-testid="fv-print" aria-label={t("fv_print")} className={btn}><Printer className="h-5 w-5" /></button>
        <button onClick={onClose} data-testid="fv-close" aria-label={t("close")} className={btn}>
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-2"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {err ? (
          <p className="text-sm font-semibold text-white/70" data-testid="fv-error">{t("toast_failed")}</p>
        ) : !url ? (
          <Loader2 className="h-8 w-8 animate-spin text-white/60" data-testid="fv-loading" />
        ) : isPdf || isWeb ? (
          <iframe src={url} title={title} className="h-full w-full rounded-xl bg-white" data-testid="fv-frame" />
        ) : (
          <img src={url} alt={title} className="max-h-full max-w-full rounded-xl object-contain" data-testid="fv-img" />
        )}
      </div>
    </div>,
    document.body
  );
}
